import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import type {
  AdditionalRequiredDocument,
  RequiredCreditRatingDocument,
  RequiredDocument,
} from 'src/apis/assignment-service/types';
import type { AssignmentInfoRequest } from 'src/apis/assignment-service/use-assignment-info';
import { useDownloadRequiredInformation } from 'src/apis/assignment-service/use-download-required-document';
import type { PublicRequiredDocumentsRequest } from 'src/apis/assignment-service/use-public-required-documents';
import type { PublicOperatorDetailsResponse } from 'src/apis/organisation-service/types';
import type { OptionalShipperDetailsRequest } from 'src/apis/organisation-service/use-optional-shipper-details';
import type { OptionalOrganisationDetailsRequest } from 'src/apis/organisation-service/use-organisation-details';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { Divider } from 'src/components/dividers';
import { Alert } from 'src/components/feedback/alert';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import { DeliveryMethodLabels } from 'src/pages/settings/assignment-settings/required-information/edit/schema';

type Props = {
  publicRequiredDocuments: Successful<PublicRequiredDocumentsRequest>;
  tsoDetails: PublicOperatorDetailsResponse;
  assignmentInfo: Successful<AssignmentInfoRequest>;
  organisationDetails?: Successful<OptionalOrganisationDetailsRequest>;
  shipperDetails?: Successful<OptionalShipperDetailsRequest>;
};

export const CompanyRequiredDocuments: FC<Props> = ({
  publicRequiredDocuments,
  tsoDetails,
  assignmentInfo,
  organisationDetails,
  shipperDetails,
}) => {
  const { additionalOrganisationDocuments, additionalUserDocuments } =
    publicRequiredDocuments.response.data;

  const hasDownloadableDocs = (docs: AdditionalRequiredDocument[]): boolean =>
    docs.some((doc) => doc.document?._links.download.href != null);

  const hasAdditionalDocs =
    hasDownloadableDocs(additionalOrganisationDocuments) ||
    hasDownloadableDocs(additionalUserDocuments);
  return (
    <Card id="documents" data-help-annotation="assignment-documents:1">
      <div
        className={`
          grid items-center gap-md
          legacy:gap-[1rem]
        `}
      >
        <Heading mode="title" size="md">
          Documents
        </Heading>
        {hasAdditionalDocs && (
          <Alert type="info">
            Content available for download has been provided by the operator.
            Users are encouraged to take appropriate precautions, such as virus
            scans and malware checks, before accessing the files. PRISMA assumes
            no responsibility for any issues or security risks that may arise
            from using these files.
          </Alert>
        )}
        <Divider />
        <ExternalLink
          target="_blank"
          mode="default-underlined"
          rel="noopener noreferrer"
          hideIcon={true}
          href={tsoDetails.gtcUrl}
        >
          General Terms and Conditions <FontAwesomeIcon icon={faExternalLink} />
        </ExternalLink>
        <Divider />

        <AssignmentPublicRequiredDocuments
          publicRequiredDocuments={publicRequiredDocuments}
          assignmentInfo={assignmentInfo}
          organisationDetails={organisationDetails}
          shipperDetails={shipperDetails}
        />
      </div>
    </Card>
  );
};

const AssignmentPublicRequiredDocuments: FC<{
  publicRequiredDocuments: Successful<PublicRequiredDocumentsRequest>;
  assignmentInfo: Successful<AssignmentInfoRequest>;
  organisationDetails?: Successful<OptionalOrganisationDetailsRequest>;
  shipperDetails?: Successful<OptionalShipperDetailsRequest>;
}> = ({
  publicRequiredDocuments,
  assignmentInfo,
  organisationDetails,
  shipperDetails,
}) => {
  const { data } = publicRequiredDocuments.response;

  const anyRequiredDocuments: boolean = useMemo(
    () =>
      [
        data.companyRegisterExtract,
        data.currentCompanyReport,
        data.mostRecentFullYearFinancialReport,
        data.mostRecentInterimFinancialReport,
        data.creditSolvencyCheck,
        data.poa,
        data.creditRating,
      ].some((doc) => doc && doc.acceptedDeliveryMethods.length > 0),
    [data]
  );

  return (
    <div
      className={`
        grid items-center gap-xs
        legacy:gap-[0.5rem]
      `}
    >
      <p>
        Following documents are required when requesting an assignment with this
        operator:
      </p>

      <DocumentTitle>New Organisation Must Provide</DocumentTitle>
      {!anyRequiredDocuments &&
        data.additionalOrganisationDocuments.length === 0 && (
          <EmptyValue label="There are no documents to show." />
        )}
      {anyRequiredDocuments && (
        <div
          className={`
            grid items-center gap-xs
            legacy:gap-[0.5rem]
          `}
          data-testid="documents-list"
        >
          {data.companyRegisterExtract && (
            <DocumentView
              label="Company Register Extract"
              document={data.companyRegisterExtract}
            />
          )}
          {data.currentCompanyReport && (
            <DocumentView
              label="Current Company Report"
              document={data.currentCompanyReport}
            />
          )}
          {data.mostRecentFullYearFinancialReport && (
            <DocumentView
              label="Most Recent (Full-Year) Financial Report"
              document={data.mostRecentFullYearFinancialReport}
            />
          )}
          {data.mostRecentInterimFinancialReport && (
            <DocumentView
              label="Most Recent (Interim) Financial Report"
              document={data.mostRecentInterimFinancialReport}
            />
          )}
          {data.creditSolvencyCheck && (
            <DocumentView
              label="Credit/Solvency Check Document"
              document={data.creditSolvencyCheck}
            />
          )}
          {data.poa && (
            <DocumentView
              label="PRISMA Shipper Admin Power of Attorney"
              document={data.poa}
            />
          )}
          {data.creditRating && (
            <DocumentView
              label="Credit Rating Certification"
              document={data.creditRating}
              agency={data.creditRating.agency}
            />
          )}
        </div>
      )}
      {data.additionalOrganisationDocuments.length > 0 && (
        <div
          className={`
            grid items-center gap-xs
            legacy:gap-[0.5rem]
          `}
          data-testid="custom-documents-list"
        >
          {data.additionalOrganisationDocuments.map((organisationDocument) => (
            <CustomDocumentView
              key={organisationDocument.name}
              additionalDocument={organisationDocument}
            />
          ))}
        </div>
      )}
      <DocumentTitle>New Users Must Provide</DocumentTitle>
      {data.additionalUserDocuments.length === 0 && (
        <EmptyValue label="There are no documents to show." />
      )}
      {data.additionalUserDocuments.length > 0 && (
        <div
          className={`
            grid items-center gap-xs
            legacy:gap-[0.5rem]
          `}
          data-testid="user-documents-list"
        >
          {data.additionalUserDocuments.map((userDocument) => (
            <CustomDocumentView
              key={userDocument.name}
              additionalDocument={userDocument}
            />
          ))}
        </div>
      )}
      {assignmentInfo.response.data.dvgwCodeRequiredForAssignment && (
        <div
          className={`
            grid items-center gap-md
            legacy:gap-[1rem]
          `}
          data-testid="dvgw-code-configuration"
        >
          <Divider />
          <p data-testid="dvgw-code-message">
            This operator needs a supplier DVGW code for an assignment.
            {/* check if the shipper is logged-in and has set the DVGW code */}
            {organisationDetails?.response.data._links.shipperDetails &&
              !shipperDetails?.response.data.supplierUserDvgwCode && (
                <span data-testid="dvgw-code-hint">
                  {' '}
                  You have to provide this code in your{' '}
                  <Link
                    mode="default-underlined"
                    to="/settings/organisation/company-information"
                    data-testid="dvgw-code-link"
                  >
                    Shipper Details
                  </Link>
                  .
                </span>
              )}
          </p>
        </div>
      )}
    </div>
  );
};

export const DocumentTitle: FC<{ children: ReactNode }> = ({ children }) => (
  // TODO Rupert: please do not use margin-top for layout and please use the spacing tokens instead
  <div className="mt-[1rem] grid grid-flow-col items-center justify-between">
    <strong>{children}</strong>
  </div>
);

export const DocumentList: FC<{
  items: { label: React.ReactNode; appendix?: string }[];
}> = ({ items }) => (
  <ul className="m-0">
    {items.map(({ label, appendix }, index) => (
      <li key={index}>
        {label}
        {appendix && (
          <span className="block text-[1.3rem] text-legacy-brand-light-2">
            {appendix}
          </span>
        )}
      </li>
    ))}
  </ul>
);

type DocumentViewProps = {
  label: string;
  document?: RequiredDocument | RequiredCreditRatingDocument;
  agency?: string | null;
};

export const DocumentView: FC<DocumentViewProps> = ({
  label,
  document,
  agency,
}) => {
  if (!document?.acceptedDeliveryMethods.length) return null;

  const documentItems = [
    {
      label: `${label} ${agency ? `(Agency: ${agency})` : ''}`,
      appendix: document.acceptedDeliveryMethods
        .map((method) => DeliveryMethodLabels[method])
        .join(', '),
    },
  ];

  return (
    <div className="grid grid-flow-col items-center justify-between">
      <DocumentList items={documentItems} />
    </div>
  );
};

type CustomDocumentViewProps = {
  additionalDocument: AdditionalRequiredDocument;
};

export const CustomDocumentView: FC<CustomDocumentViewProps> = ({
  additionalDocument,
}) => {
  const download = useDownloadRequiredInformation();

  const createDocumentItem = (label: React.ReactNode) => ({
    label,
    appendix: additionalDocument.acceptedDeliveryMethods
      .map((method) => DeliveryMethodLabels[method])
      .join(', '),
  });

  const documentItems = [];

  if (additionalDocument.url) {
    documentItems.push(
      createDocumentItem(
        <ExternalLink
          href={additionalDocument.url}
          target="_blank"
          mode="default-underlined"
          rel="noopener noreferrer"
        >
          {additionalDocument.name}
        </ExternalLink>
      )
    );
  }

  if (additionalDocument.document?._links.download.href) {
    documentItems.push(
      createDocumentItem(
        <Tooltip
          content="Content available for download has been provided by the operator. PRISMA assumes no responsibility for any issues or security risks that may arise from using these files."
          noTabIndex
        >
          {(targetProps) => (
            <Button
              mode="link-underlined"
              behaviour="download"
              onClick={() =>
                download.execute({
                  url: additionalDocument.document!._links.download.href,
                })
              }
              disabled={download.pending}
              {...targetProps}
            >
              {additionalDocument.name}
            </Button>
          )}
        </Tooltip>
      )
    );
  }

  if (!additionalDocument.url && !additionalDocument.document) {
    documentItems.push(createDocumentItem(additionalDocument.name));
  }

  return <DocumentList items={documentItems} />;
};
